import * as React from 'preact/compat'
import css from '../../../css/v9/components/video-player.pcss.json'
import { loadStylesheet } from '../../common/helpers'
import SVG from '../SVG'
import Loading from '../Loading'
import cn from 'classnames'
import RangeSlider from './RangeSlider'
import Settings from './Settings'
import OSD from './OSD'

declare global {
  interface Document {
    webkitExitFullscreen?: () => Promise<void>
  }

  interface HTMLElement {
    webkitRequestFullscreen?: () => Promise<void>
  }

  interface HTMLVideoElement {
    webkitEnterFullscreen?: () => Promise<void>
  }
}

interface PropTypes {
  poster: string,
  files: string[],
  title?: string
}

interface StateTypes {
  started: boolean,
  paused: boolean,
  buffering: boolean,
  ended: boolean,
  muted: boolean,
  volume: number,
  currentTime: number,
  totalDuration: number,
  settingsShown: boolean,
  isFullscreen: boolean,
  currentResolution: string,
  controlsShown: boolean
}

const resolutionRegex = /^\[(\d+p)\]/

export default class VideoPlayer extends React.Component<PropTypes, StateTypes> {

  container = React.createRef<HTMLDivElement>()
  video = React.createRef<HTMLVideoElement>()
  controls = React.createRef<HTMLDivElement>()
  settings = React.createRef<HTMLButtonElement>()
  settingsMenu = React.createRef<Settings>()
  playBtn = React.createRef<HTMLButtonElement>()
  osd = React.createRef<OSD>()
  showControlsTimeout: number

  constructor(props: PropTypes) {
    super(props)

    const volume = parseFloat(window.localStorage.getItem('videoVolume') || '1')
    const muted = Math.floor(volume * 100) === 0

    this.state = {
      started: false,
      paused: false,
      buffering: false,
      ended: false,
      muted: muted,
      volume: parseFloat(window.localStorage.getItem('videoVolume') || '1'),
      currentTime: 0,
      totalDuration: 0,
      settingsShown: false,
      isFullscreen: false,
      currentResolution: this.detectBestVideoResolution(),
      controlsShown: false
    }
  }

  detectBestVideoResolution = () => {
    const availableResolutions = this.props.files.map((file) => {
      const matches = file.match(resolutionRegex)
      if (!matches) {
        return '360p'
      }
      return matches[1]
    })

    if (!navigator.connection) {
      console.warn('Network Information API not supported');
      return availableResolutions.at(-1);
    }

    const { effectiveType } = navigator.connection;

    let resolution: string;

    switch (effectiveType) {
      case 'slow-2g':
      case '2g':
        resolution = '360p';
        break;
      case '3g':
        resolution = '480p';
        break;
      case '4g':
        resolution = '720p';
        break;
      default:
        resolution = '1080p';
    }

    return availableResolutions.includes(resolution) ? resolution : availableResolutions.at(-1);
  }

  componentWillMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('v9/components/video-player')
  }

  componentDidMount() {
    const { current: video } = this.video
    const { current: container } = this.container
    const { current: controls } = this.controls
    video.addEventListener('play', () => {
      video.volume = this.state.volume
      this.setState({
        started: true,
        paused: false,
        ended: false
      })
      this.showControls()
    })
    video.addEventListener('pause', () => {
      if (this.showControlsTimeout) {
        clearTimeout(this.showControlsTimeout)
      }
      this.setState({
        paused: true,
        controlsShown: true
      })
    })
    video.addEventListener('waiting', () => {
      this.setState({
        buffering: true
      })
    })
    video.addEventListener('playing', () => {
      this.setState({
        buffering: false,
        ended: false
      })
    })
    video.addEventListener('ended', () => {
      this.setState({
        ended: true
      })
    })
    video.addEventListener('volumechange', () => {
      const muted = video.volume === 0 || video.muted
      const volume = muted ? 0 : video.volume

      window.localStorage.setItem('videoVolume', volume.toString())

      this.setState({
        muted,
        volume
      })
    })
    video.addEventListener('timeupdate', () => {
      this.setState({
        currentTime: video.currentTime,
        ended: video.currentTime === video.duration
      })
    })
    video.addEventListener('loadedmetadata', () => {
      this.setState({
        totalDuration: video.duration
      })
    })
    container.addEventListener('fullscreenchange', () => {
      if (document.fullscreenElement === null) {
        screen.orientation.unlock()
      }
      this.setState({
        isFullscreen: document.fullscreenElement !== null
      })
    })
    container.addEventListener('mousemove', () => {
      if (window.matchMedia('(hover: hover)').matches) {
        this.showControls()
      }
    })
    container.addEventListener('mouseleave', () => {
      this.hideControls()
    })
    controls.addEventListener('focusin', () => {
      this.registerHotkeys()
      // Это фокус мышью — игнорируем
      if (controls.parentElement.querySelector(`.${css['controls']}:hover`)) {
        return
      }
      this.showControls(false)
    })
    controls.addEventListener('focusout', () => {
      // Таймаут, чтобы сперва выставился фокус на другом элементе и можно было проверить, не ушёл ли фокус вне контролов
      setTimeout(() => {
        if (controls.contains(document.activeElement) || controls.parentElement.querySelector(`.${css['controls']}:hover`)) {
          return
        }
        this.unregisterHotkeys()
        this.hideControls()
      }, 1)
    })
    window.addEventListener('click', (e) => {
      if (container.contains(e.target as Node)) {
        return
      }
      this.unregisterHotkeys()
    })
    window.addEventListener('sg-stop-video', () => {
      video.pause()
    })
  }

  componentDidUpdate(_previousProps: Readonly<PropTypes>, previousState: Readonly<StateTypes>) {
    if (previousState.currentResolution !== this.state.currentResolution) {
      const { current: video } = this.video
      const currentTime = video.currentTime
      video.load()
      video.currentTime = currentTime
      if (!this.state.paused) {
        // noinspection JSIgnoredPromiseFromCall
        video.play()
      }
    }
  }

  registerHotkeys = () => {
    window.addEventListener('keydown', this.handleKeyDown)
  }

  unregisterHotkeys = () => {
    window.removeEventListener('keydown', this.handleKeyDown)
  }

  handleKeyDown = (e: KeyboardEvent) => {
    const { current: video } = this.video
    if (e.key === ' ' && !this.controls.current.contains(document.activeElement)) {
      e.preventDefault()
      this.playPause()
      this.showOSD({
        icon: this.state.paused ? 'player/play' : 'player/pause'
      })
    }
    if (!this.state.started) {
      return
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      video.currentTime -= 5
      this.showOSD({
        icon: 'player/rewind',
      })
      this.showControls()
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      video.currentTime += 5
      this.showOSD({
        icon: 'player/fast-forward',
      })
      this.showControls()
    }
    if (e.key === 'ArrowUp') {
      if (this.settingsMenu.current && this.settingsMenu.current.hasKeyboardFocus()) {
        return
      }
      e.preventDefault()
      video.volume = Math.min(video.volume + 0.1, 1)
      video.muted = false
      this.showOSD({
        icon: 'player/volume',
        text: Math.round(video.volume * 100) + '%'
      })
      this.showControls()
    }
    if (e.key === 'ArrowDown') {
      if (this.settingsMenu.current && this.settingsMenu.current.hasKeyboardFocus()) {
        return
      }
      e.preventDefault()
      video.volume = Math.round(Math.max(video.volume - 0.1, 0) * 100) / 100
      video.muted = video.volume === 0
      let icon = 'player/volume-low'
      if (video.muted) {
        icon = 'player/volume-off'
      }
      this.showOSD({
        icon,
        text: (video.volume * 100) + '%'
      })
      this.showControls()
    }
    if (e.key === 'm') {
      e.preventDefault()
      let icon = 'player/volume-off'
      let text = '0%'
      if (this.state.muted) {
        icon = 'player/volume'
        text = Math.round((video.volume || 1) * 100) + '%'
      }
      this.toggleMute()
      this.showOSD({
        icon,
        text
      })
      this.showControls()
    }
    if (e.key === 'f') {
      e.preventDefault()
      this.toggleFullscreen()
    }
    if (e.key === 'Escape' && !this.state.isFullscreen) {
      e.preventDefault()
      this.hideControls()
    }
  }

  renderSources() {
    const sources = []

    this.props.files.forEach((source) => {
      const matches = source.match(resolutionRegex)
      let resolution = '360p'
      if (matches) {
        resolution = matches[1]
      }
      if (resolution == this.state.currentResolution) {
        source = source.replace(resolutionRegex, '')
        sources.push(<source key={`${resolution}-m4v`} src={source} type='video/x-m4v'/>)
        sources.push(<source key={resolution} src={source} type='video/mp4'/>)
      }
    })

    return sources
  }

  changeResolution = (resolution: string) => {
    this.setState({
      currentResolution: resolution,
      settingsShown: false
    })
  }

  play = (e: React.TargetedEvent & MouseEvent, focusPlay?: true) => {
    e.stopImmediatePropagation()
    // noinspection JSIgnoredPromiseFromCall
    this.video.current.play()
    if (focusPlay) {
      setTimeout(this.focusPlay, 1)
    }
  }

  playPause = (e?: React.TargetedEvent & MouseEvent) => {
    e?.stopImmediatePropagation()
    const { current: video } = this.video
    if (this.state.paused || !this.state.started) {
      // noinspection JSIgnoredPromiseFromCall
      video.play()
    } else {
      video.pause()
    }
  }

  hideSettings = () => {
    this.setState({
      settingsShown: false
    })
  }

  toggleSettings = (e: React.TargetedEvent<HTMLButtonElement> & MouseEvent) => {
    e.stopImmediatePropagation()
    this.setState({
      settingsShown: !this.state.settingsShown
    })
  }

  toggleMute = (e?: React.TargetedEvent<HTMLButtonElement> & MouseEvent) => {
    e?.stopImmediatePropagation()
    const { current: video } = this.video
    video.muted = !this.state.muted
    if (video.volume === 0 && this.state.muted) {
      video.volume = 1
    }
  }

  setVolume = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopImmediatePropagation()
    const { current: video } = this.video
    const volume = parseFloat(event.currentTarget.value)
    video.muted = volume === 0
    video.volume = volume
  }

  seek = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopImmediatePropagation()
    this.video.current.currentTime = parseFloat(event.currentTarget.value) / 10
  }

  requestFullscreen(container: HTMLDivElement, video: HTMLVideoElement) {
    if (container.requestFullscreen) {
      return container.requestFullscreen()
    }
    if (container.webkitRequestFullscreen) {
      return container.webkitRequestFullscreen()
    }
    if (video.webkitEnterFullscreen) {
      return video.webkitEnterFullscreen()
    }
    return Promise.reject()
  }

  exitFullscreen() {
    if (document.exitFullscreen) {
      return document.exitFullscreen()
    }
    if (document.webkitExitFullscreen) {
      return document.webkitExitFullscreen()
    }
    return Promise.reject()
  }

  toggleFullscreen = (e?: React.TargetedEvent<HTMLButtonElement> & MouseEvent) => {
    e?.stopImmediatePropagation()
    const { current: container } = this.container
    const { current: video } = this.video
    const { isFullscreen } = this.state
    if (isFullscreen) {
      this.exitFullscreen()
        ?.then(() => screen.orientation.unlock())
        .catch(() => {
        })
    } else {
      this.requestFullscreen(container, video)
        // @ts-ignore
        ?.then(() => screen.orientation.lock("landscape-primary"))
        .catch(() => {
        })
    }
    this.setState({
      isFullscreen: !isFullscreen
    })
  }

  formatTime = (time: number, maxTime?: number) => {
    const hours = Math.floor(time / 3600)
    let hasHours = hours > 0
    let minutes = (Math.floor(time / 60) % 60).toString()
    const seconds = Math.floor(time % 60).toString().padStart(2, '0')

    if (maxTime) {
      const maxTimeHours = Math.floor(maxTime / 3600)
      hasHours = hasHours || maxTimeHours > 0
    }

    if (hasHours) {
      minutes = minutes.padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    }

    return `${minutes}:${seconds}`
  }

  showControls = (useTimeout: boolean = true) => {
    this.setState({
      controlsShown: true
    })
    if (this.showControlsTimeout) {
      clearTimeout(this.showControlsTimeout)
    }
    if (useTimeout) {
      // @ts-ignore
      this.showControlsTimeout = setTimeout(this.hideControls, 3000)
    }
  }

  hideControls = () => {
    if (this.state.paused) {
      return
    }
    this.setState({
      controlsShown: false,
      settingsShown: false
    })
  }

  controlsClick = (e: React.TargetedEvent<HTMLDivElement> & MouseEvent) => {
    e.stopImmediatePropagation()
    const { controlsShown } = this.state

    this.registerHotkeys()

    if (controlsShown) {
      this.playPause(e)
      this.showOSD({
        icon: this.state.paused ? 'player/play' : 'player/pause'
      })
    } else {
      this.showControls()
    }
  }

  focusSettings = () => {
    this.settings.current.focus()
  }

  focusPlay = () => {
    if (this.playBtn.current) {
      this.playBtn.current.focus()
    }
  }

  showOSD = (osd: { icon: string, text?: string }) => {
    this.osd.current.show(osd)
  }

  volumeWheel = (e: WheelEvent) => {
    e.preventDefault()
    const { current: video } = this.video
    video.volume = Math.max(Math.min(video.volume + (-1 * e.deltaY) / 1000, 1), 0)
    video.muted = video.volume === 0
    this.showControls()
  }

  render() {
    const {
      started,
      paused,
      settingsShown,
      buffering,
      ended,
      muted,
      volume,
      currentTime,
      totalDuration,
      isFullscreen,
      controlsShown,
      currentResolution
    } = this.state
    const { poster, files, title } = this.props

    let volumeIcon = 'volume-high'

    if (volume === 0 || muted) {
      volumeIcon = 'volume-off'
    } else if (volume < 0.33) {
      volumeIcon = 'volume-low'
    } else if (volume < 0.66) {
      volumeIcon = 'volume'
    }

    return <div className={css['player']} ref={this.container}>
      <video key='video' ref={this.video} poster={poster} playsInline={true} controls={false}>
        {this.renderSources()}
      </video>
      <div
        className={cn({
          [css['controls']]: true,
          [css['controls--shown']]: controlsShown,
          [css['title--shown']]: !started || controlsShown
        })} onClick={this.controlsClick} ref={this.controls}
      >
        {title && <span className={css['title']}><span>{title}</span></span>}
        {!started && <button className={css['play-btn']} onClick={(e) => this.play(e, true)}>
          <SVG i='player/play-btn'/>
        </button>}
        {buffering && <Loading className={css['loading']} string=' '/>}
        {started && <div className={css['bar']} onClick={(e) => e.stopImmediatePropagation()}>
          <RangeSlider
            min='0'
            max={totalDuration * 10}
            value={currentTime * 10}
            className={css['seek']}
            onChange={this.seek}
          />
          <button className={cn(css['controls-btn'], css['play'])} onClick={this.playPause} ref={this.playBtn}>
            <SVG i={ended ? 'player/restart' : paused ? 'player/play' : 'player/pause'}/>
          </button>
          <div className={css['volume']}>
            <button className={css['controls-btn']} onClick={this.toggleMute}>
              <SVG i={`player/${volumeIcon}`}/>
            </button>
            <RangeSlider
              min='0'
              max='1'
              step='0.01'
              value={volume}
              className={css['volume-bar']}
              onChange={this.setVolume}
              onWheel={this.volumeWheel}
            />
          </div>
          <span className={css['time']}>{this.formatTime(currentTime)} / {this.formatTime(totalDuration)}</span>
          <button
            className={cn({
              [css['controls-btn']]: true,
              [css['settings']]: true,
              [css['settings--active']]: settingsShown
            })} onClick={this.toggleSettings} ref={this.settings}
          >
            <SVG i='player/settings'/>
          </button>
          <button className={cn(css['controls-btn'], css['fullscreen'])} onClick={this.toggleFullscreen}>
            <SVG i={isFullscreen ? 'player/contract' : 'player/expand'}/>
          </button>
        </div>}
        {settingsShown && <Settings
          ref={this.settingsMenu}
          files={files}
          hideSettings={this.hideSettings}
          changeResolution={this.changeResolution}
          currentResolution={currentResolution}
          focusSettings={this.focusSettings}
        />}
        <OSD ref={this.osd}/>
      </div>
    </div>
  }
}
